<template>
    <PaymentAccountsTable>
        <template #actions>
            <DeleteBulkButton
                v-if="userStore.can('delete_payment_accounts')"
                :selected="selected"
                @click.stop="handleDeletePaymentAccounts"
            />
            <CreateButton
                v-if="userStore.can('create_payment_account')"
                @click.stop="handleCreatePaymentAccount"
            />
        </template>
        <template #filters>
            <DropdownFilter
                :pin="pinFilters"
                @apply="applyFilters"
                @clear="clearFilters"
                @toggle-pin="togglePinFilters"
            >
                <DropdownFilterItem v-if="userStore.can('filter_by_id')">
                    <label class="flex items-center">
                        <TextField
                            v-model="filtersFields.id"
                            name="id"
                            :placeholder="$t('id')"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.type"
                            :options="mapEnumToArray(PaymentAccountType)"
                            name="type"
                            class="w-full"
                            :placeholder="$t('type')"
                            :disabled="disableFilter('id')"
                            :on-search="loadPaymentAccountTypeSelectOptions"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.platform_name"
                            v-model:options="platformsSelectOptions"
                            name="platform_name"
                            class="w-full"
                            :placeholder="$t('platform')"
                            :disabled="disableFilter('platform_name')"
                            :on-search="loadPlatformsSelectOptions"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <Datepicker
                            v-model="filtersFields.created_at"
                            placeholder="created_on"
                            name="created_on"
                            :disabled="disableFilter('created_at')"
                        />
                    </label>
                </DropdownFilterItem>
            </DropdownFilter>
        </template>
    </PaymentAccountsTable>

    <ModalWrapper v-if="isModalOpen" />
</template>

<script setup lang="ts">
import moment from 'moment'

import {
    ModalButtonStyles,
    useEnum,
    useModal,
    useTable,
} from '@composables/common'
import {
    Datepicker,
    DropdownFilter,
    DropdownFilterItem,
    NameAndId,
    SelectField,
    TextField,
} from '@src/components'
import { PaymentAccountType } from '@src/enums/PaymentAccount'
import CreateButton from '@src/partials/actions/CreateButton.vue'
import DeleteBulkButton from '@src/partials/actions/DeleteBulkButton.vue'
import { Platform, TableSorting } from '@src/types'
import { PaymentAccount } from '@src/types/PaymentAccount'
import {
    deletePaymentAccount as apiDeletePaymentAccount,
    deletePaymentAccounts as apiDeletePaymentAccounts,
    getPaymentAccounts,
} from '@src/utils/api/paymentAccounts'
import { getPlatforms } from '@src/utils/api/platforms'
import { useToastStore } from '@src/utils/stores/toast'
import { useUserStore } from '@src/utils/stores/user'
import { computed, h, onMounted, ref, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
const { mapEnumToArray } = useEnum()

const props = withDefaults(
    defineProps<{
        initialFilters?: {
            id?: string | null
            type?: string | null
            created_at?: string | null
            platform_name?: string | null
        } | null
    }>(),
    {
        initialFilters: null,
    }
)

const { initialFilters } = toRefs(props)

const router = useRouter()
const toast = useToastStore()
const i18n = useI18n()
const { ModalWrapper, openModal } = useModal()

const paymentAccounts = ref<PaymentAccount[]>([])
const paymentAccountsCount = ref<number>(0)

const userStore = useUserStore()

const disableFilter = (filter: string) => {
    if (initialFilters.value) {
        return (
            Object.keys(initialFilters.value).filter((item) => {
                return item === filter
            }).length > 0
        )
    }
    return false
}

const {
    TableWrapper: PaymentAccountsTable,
    selected,
    limit,
    sorting,
    page,
    filtersFields,
    filters,
    pinFilters,
    togglePinFilters,
    setFilters,
    applyFilters,
    clearFilters,
} = useTable({
    loadData: loadPaymentAccounts,
    filters: {
        id: null,
        type: null,
        created_at: null,
    },
    disableFilters: initialFilters.value
        ? Object.keys(initialFilters.value)
        : [],
    tableProps: {
        id: 'payment-accounts-table',
        label: 'payment_accounts',
        help: 'help_payment_accounts_table',
        pagination: true,
        data: paymentAccounts,
        total: paymentAccountsCount,
        sorting: ref<TableSorting[]>([
            {
                field: 'created_at',
                direction: 'desc',
            },
        ]),
        columns: [
            {
                label: 'id',
                key: 'id',
                select: true,
            },
            {
                label: 'id',
                key: 'id',
                url: paymentAccountsTableUrl,
                sorting: true,
                condition: userStore.can('see_id_columns'),
                copy: true,
            },
            {
                label: 'name',
                key: 'name',
                sorting: true,
                copy: true,
            },
            {
                label: 'type',
                key: 'type',
                sorting: true,
                copy: true,
            },
            {
                label: 'created_on',
                key: 'created_at',
                sorting: true,
                cellRenderer: (props: { rowData: PaymentAccount }) => {
                    return moment(props.rowData.created_at).format('DD.MM.yyyy')
                },
            },
        ],
        actions: [
            {
                action: handleViewPaymentAccount,
                icon: 'fa-eye',
                title: 'view',
                condition: userStore.can('view_payment_accounts'),
            },
            {
                action: handleEditPaymentAccount,
                icon: 'fa-solid fa-pen',
                title: 'edit',
                condition: userStore.can('update_payment_account'),
            },

            {
                action: handleDeletePaymentAccount,
                icon: 'fas fa-trash-alt',
                title: 'delete',
                color: 'text-danger-500',
                condition: userStore.can('delete_payment_account'),
            },
        ],
    },
})

const isModalOpen = ref(false)

const selectedPaymentAccounts = computed(() => {
    return paymentAccounts.value.filter(
        (item: PaymentAccount) => item.id && selected.value.includes(item.id)
    )
})

const platformsSelectOptions = ref<string[]>([])

async function loadPlatformsSelectOptions(search: string) {
    const initialPlatformsRequest = await getPlatforms({
        'filter[platform_name]': search,
    })
    platformsSelectOptions.value = initialPlatformsRequest.data.platforms.map(
        (platform: Platform) => {
            return platform.platform_name as string
        }
    )
}

function paymentAccountsTableUrl(paymentAccount: PaymentAccount) {
    return '/payment-accounts/' + paymentAccount.id
}

function handleViewPaymentAccount(paymentAccount: PaymentAccount) {
    router.push({
        name: 'payment_account',
        params: { id: paymentAccount.id },
    })
}

function handleEditPaymentAccount(paymentAccount: PaymentAccount) {
    router.push({
        name: 'payment_account.edit',
        params: { id: paymentAccount.id },
    })
}

function handleDeletePaymentAccount(paymentAccount: PaymentAccount) {
    isModalOpen.value = true
    openModal(
        {
            id: 'delete_payment_account-modal',
            title: 'modal_title_delete_payment_account',
            open: isModalOpen,
            onCancel: () => (isModalOpen.value = false),
            onSubmit: async () => await deletePaymentAccount(paymentAccount),
        },
        () =>
            h(NameAndId, {
                idKey: 'id',
                nameKey: 'type',
                modelValue: [paymentAccount],
            }),
        {
            submitButtonText: 'delete',
            styles: {
                submitButton: ModalButtonStyles['RED'],
            },
        }
    )
}

function handleDeletePaymentAccounts() {
    isModalOpen.value = true
    openModal(
        {
            id: 'delete_payment_accountss',
            title: 'modal_title_delete_payment_accounts',
            open: isModalOpen,
            onCancel: () => (isModalOpen.value = false),
            onSubmit: () => deletePaymentAccounts(),
        },
        () =>
            h(NameAndId, {
                idKey: 'id',
                nameKey: 'type',
                modelValue: selectedPaymentAccounts.value,
            }),
        {
            submitButtonText: 'delete',
            styles: {
                submitButton: ModalButtonStyles['RED'],
            },
        }
    )
}

function duplicatePaymentAccount(paymentAccount: PaymentAccount) {
    isModalOpen.value = false
    router.push({
        name: 'payment_account.copy',
        params: { copyUid: paymentAccount.id },
    })
}

async function deletePaymentAccount(paymentAccount: PaymentAccount) {
    await apiDeletePaymentAccount(paymentAccount)
    toast.success(
        i18n.t('toast_success_payment_account_deleted', {
            paymentAccount: paymentAccount.id,
        })
    )
    isModalOpen.value = false
    await loadPaymentAccounts()
}

async function deletePaymentAccounts() {
    await apiDeletePaymentAccounts(selected.value)
    toast.success(
        i18n.t('toast_success_payment_accounts_deleted', {
            paymentAccounts: selected.value.join(', '),
        })
    )
    isModalOpen.value = false
    selected.value = []
    await loadPaymentAccounts()
}

async function loadPaymentAccounts() {
    const { data } = await getPaymentAccounts(
        filters.value,
        page.value,
        limit.value,
        sorting.value
    )
    paymentAccounts.value = data.payment_accounts
    paymentAccountsCount.value = data.payment_accounts_count
    selected.value = []
}

function handleCreatePaymentAccount() {
    router.push({
        name: 'payment_account.create',
    })
}

const paymentAccountTypeSelectOptions = ref<
    { label: string; value: string | null }[]
>([])

async function loadPaymentAccountTypeSelectOptions(search: string) {
    const paymentAccountRequest = await getPaymentAccounts({
        'filter[type]': search,
    })
    paymentAccountTypeSelectOptions.value =
        paymentAccountRequest.data.payment_accounts.map(
            (paymentAccount: PaymentAccount) => {
                return {
                    label: paymentAccount.type as string,
                    value: paymentAccount.type,
                }
            }
        )
}

onMounted(async () => {
    if (initialFilters.value) {
        filtersFields.value = {
            ...filtersFields.value,
            ...initialFilters.value,
        }
        if (filtersFields.value.id) {
            await loadPlatformsSelectOptions(filtersFields.value.id as string)
        }
        setFilters()
    }
    await loadPaymentAccounts()
})
</script>

<style lang="scss" scoped>
input {
    &:disabled {
        @apply disabled:bg-gray-100;
    }
}
</style>
